/**
 * NGX ETF fee schedule used by NigaETF Trader.
 *
 * NGX X-Compliance lists ETFs at the same core fee levels as equities with
 * two differences:
 *   - Brokerage is typically negotiated lower (retail default 1.00% vs 1.35%)
 *   - CSCS fee for ETFs is 0.06% (same as equities — CSCS doesn't give a
 *     discount to ETFs on NGX as at 2026-Q1).
 *
 * Stamp Duty at 0.075% is BUY-only and is applied to the consideration
 * (units * last-traded-price) in accordance with the Stamp Duties Act.
 *
 * All math uses bigint kobo — no floats.
 */

export type Side = 'BUY' | 'SELL';

export interface EtfFeeRates {
  brokerageBps: bigint;   // 100 = 1.00%
  secBps: bigint;         // 30  = 0.30%
  cscsBps: bigint;        // 6   = 0.06%
  stampDutyBps: bigint;   // 75  = 0.075% (divided by 100_000)
  terminalBps: bigint;    // 25  = 0.25%
  vatBrokeragePctBps: bigint; // 750 = 7.5%
  vatSecPctBps: bigint;       // 750 = 7.5%
  vatCscsPctBps: bigint;      // 500 = 5%
}

export const DEFAULT_ETF_RATES: EtfFeeRates = {
  brokerageBps: 100n,
  secBps: 30n,
  cscsBps: 6n,
  stampDutyBps: 75n,
  terminalBps: 25n,
  vatBrokeragePctBps: 750n,
  vatSecPctBps: 750n,
  vatCscsPctBps: 500n,
};

export interface EtfFeeBreakdown {
  side: Side;
  consideration: string;
  brokerage: string;
  vatOnBrokerage: string;
  secFee: string;
  vatOnSec: string;
  cscsFee: string;
  vatOnCscs: string;
  stampDuty: string;
  terminalFee: string;
  totalFees: string;
  totalCost: string;
  rates: {
    brokeragePct: string;
    secPct: string;
    cscsPct: string;
    stampDutyPct: string;
    terminalPct: string;
    vatBrokeragePct: string;
    vatSecPct: string;
    vatCscsPct: string;
  };
}

function ofBps(amount: bigint, bps: bigint): bigint { return (amount * bps) / 10000n; }
function ofStampBps(amount: bigint, bps: bigint): bigint { return (amount * bps) / 100000n; }
function bpsToPct(bps: bigint, denom = 10000n): string {
  const num = Number(bps) / (Number(denom) / 100);
  const str = num.toFixed(4).replace(/\.?0+$/, '');
  return str === '' ? '0' : str;
}

/**
 * Compute the full NGX ETF fee breakdown.
 * @param units  Number of ETF units (integer)
 * @param priceKobo Price per unit in kobo (integer string)
 */
export function computeEtfFees(
  side: Side,
  units: number,
  priceKobo: string,
  rates: EtfFeeRates = DEFAULT_ETF_RATES,
): EtfFeeBreakdown {
  if (!Number.isInteger(units) || units <= 0) throw new Error('units must be a positive integer');
  if (!/^\d+$/.test(priceKobo)) throw new Error('priceKobo must be integer kobo');

  const u = BigInt(units);
  const px = BigInt(priceKobo);
  const consideration = u * px;

  const brokerage = ofBps(consideration, rates.brokerageBps);
  const secFee = ofBps(consideration, rates.secBps);
  const cscsFee = ofBps(consideration, rates.cscsBps);
  const stampDuty = side === 'BUY' ? ofStampBps(consideration, rates.stampDutyBps) : 0n;
  const terminalFee = ofBps(consideration, rates.terminalBps);

  const vatOnBrokerage = ofBps(brokerage, rates.vatBrokeragePctBps);
  const vatOnSec = ofBps(secFee, rates.vatSecPctBps);
  const vatOnCscs = ofBps(cscsFee, rates.vatCscsPctBps);

  const totalFees = brokerage + vatOnBrokerage + secFee + vatOnSec + cscsFee + vatOnCscs + stampDuty + terminalFee;
  const totalCost = side === 'BUY' ? consideration + totalFees : consideration - totalFees;

  return {
    side,
    consideration: consideration.toString(),
    brokerage: brokerage.toString(),
    vatOnBrokerage: vatOnBrokerage.toString(),
    secFee: secFee.toString(),
    vatOnSec: vatOnSec.toString(),
    cscsFee: cscsFee.toString(),
    vatOnCscs: vatOnCscs.toString(),
    stampDuty: stampDuty.toString(),
    terminalFee: terminalFee.toString(),
    totalFees: totalFees.toString(),
    totalCost: totalCost.toString(),
    rates: {
      brokeragePct: bpsToPct(rates.brokerageBps),
      secPct: bpsToPct(rates.secBps),
      cscsPct: bpsToPct(rates.cscsBps),
      stampDutyPct: bpsToPct(rates.stampDutyBps, 100000n),
      terminalPct: bpsToPct(rates.terminalBps),
      vatBrokeragePct: bpsToPct(rates.vatBrokeragePctBps),
      vatSecPct: bpsToPct(rates.vatSecPctBps),
      vatCscsPct: bpsToPct(rates.vatCscsPctBps),
    },
  };
}
